/**
 * ThemeAppearanceContext
 *
 * User-selected light/dark preference for the Radix Theme root, persisted
 * across sessions.
 *
 * Usage:
 *   // Wrap your app root:
 *   <ThemeAppearanceProvider><App /></ThemeAppearanceProvider>
 *
 *   // In any component:
 *   const { appearance, setAppearance } = useThemeAppearance();
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

export type ThemeAppearance = "dark" | "light";

const STORAGE_KEY = "dc-remote-theme-appearance";

interface ThemeAppearanceContextType {
    appearance: ThemeAppearance;
    setAppearance: (appearance: ThemeAppearance) => void;
}

const ThemeAppearanceContext = createContext<ThemeAppearanceContextType | null>(null);

export function useThemeAppearance() {
    const context = useContext(ThemeAppearanceContext);
    if (!context) {
        throw new Error("useThemeAppearance must be used within a ThemeAppearanceProvider");
    }
    return context;
}

function readStoredAppearance(): ThemeAppearance {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
    } catch {
        return "dark";
    }
}

export function ThemeAppearanceProvider({ children }: { children: ReactNode }) {
    const [appearance, setAppearanceState] = useState<ThemeAppearance>(readStoredAppearance);

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, appearance);
        } catch {
            // Best-effort persistence; the session default still works without storage.
        }

        // Radix's <Theme> only puts the "light"/"dark" class on its own root div,
        // a descendant of <body> — so a CSS variable override scoped to that div
        // (e.g. `.radix-themes.light { --dc-bg: ... }`) never reaches `body` itself;
        // CSS custom properties cascade down, not up. Mirroring the appearance here
        // as a real :root attribute lets index.css theme `body` (and everything
        // else) correctly instead of only the content inside the Theme div.
        document.documentElement.setAttribute("data-appearance", appearance);
    }, [
        appearance,
    ]);

    const setAppearance = useCallback((next: ThemeAppearance) => setAppearanceState(next), []);

    return (
        <ThemeAppearanceContext.Provider
            value={{
                appearance,
                setAppearance,
            }}
        >
            {children}
        </ThemeAppearanceContext.Provider>
    );
}
